import {
  collection,
  onSnapshot,
  orderBy,
  query,
  limit as fsLimit,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { apiClient } from './apiClient';
import {
  Broadcast,
  BroadcastInput,
  BroadcastRecipient,
  BroadcastStats,
  EMPTY_BROADCAST_STATS
} from '../../types/models/whatsapp';

const BROADCASTS = 'whatsapp_broadcasts';

/**
 * Campaigns are read live from Firestore so counters tick upward while a send
 * is in flight, and written through the API where the send pipeline lives.
 */
class BroadcastService {
  private map(id: string, data: any): Broadcast {
    return {
      id,
      name: data.name || '',
      description: data.description || '',
      templateName: data.templateName || '',
      templateLanguage: data.templateLanguage || 'en',
      variableMapping: data.variableMapping || {},
      audience: data.audience || {},
      status: data.status || 'draft',
      source: data.source || 'direct',
      scheduledAt: data.scheduledAt ?? null,
      throttlePerSecond: data.throttlePerSecond || 10,
      stats: { ...EMPTY_BROADCAST_STATS, ...(data.stats || {}) },
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt || 0,
      updatedAt: data.updatedAt || 0,
      startedAt: data.startedAt ?? null,
      completedAt: data.completedAt ?? null,
      error: data.error
    };
  }

  subscribe(
    onChange: (broadcasts: Broadcast[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    if (!db) {
      onError?.(new Error('Firestore unavailable'));
      return () => {};
    }

    const q = query(
      collection(db, BROADCASTS),
      orderBy('createdAt', 'desc'),
      fsLimit(100)
    );

    return onSnapshot(
      q,
      (snapshot) => onChange(snapshot.docs.map((d) => this.map(d.id, d.data()))),
      (error) => {
        console.error('❌ Broadcast subscription error:', error);
        onError?.(error as Error);
      }
    );
  }

  async create(input: BroadcastInput): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>('/broadcasts', input);
  }

  /** Resolves the audience into recipient rows without sending. */
  async prepare(id: string): Promise<{ stats: BroadcastStats }> {
    return apiClient.post<{ stats: BroadcastStats }>(`/broadcasts/${id}/prepare`);
  }

  async start(id: string): Promise<void> {
    await apiClient.post(`/broadcasts/${id}/start`);
  }

  async pause(id: string): Promise<void> {
    await apiClient.post(`/broadcasts/${id}/stop`, { cancel: false });
  }

  async cancel(id: string): Promise<void> {
    await apiClient.post(`/broadcasts/${id}/stop`, { cancel: true });
  }

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/broadcasts/${id}`);
  }

  async listRecipients(
    id: string,
    status?: string
  ): Promise<BroadcastRecipient[]> {
    const suffix = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await apiClient.get<{ recipients: BroadcastRecipient[] }>(
      `/broadcasts/${id}/recipients${suffix}`
    );
    return response.recipients;
  }
}

export const broadcastService = new BroadcastService();
export default broadcastService;
